/**
 * @file     badges.ts
 * @readme   ./README.md
 * @purpose  Badge catalog and eligibility checks.
 */

import '@/core/i18n/deferred/academy';
import type { Badge, BadgeId, LessonProgress, UserBadge, UserStreak, JourneyConfig } from './types';

export const BADGE_CATALOG: Badge[] = [
  {
    id: 'first-lesson',
    titleKey: 'academy.badges.firstLesson.title',
    titleDefault: 'First Steps',
    descriptionKey: 'academy.badges.firstLesson.desc',
    descriptionDefault: 'Complete your first lesson',
    icon: 'school',
  },
  {
    id: 'module-complete',
    titleKey: 'academy.badges.moduleComplete.title',
    titleDefault: 'Module Master',
    descriptionKey: 'academy.badges.moduleComplete.desc',
    descriptionDefault: 'Complete an entire module',
    icon: 'star',
  },
  {
    id: 'journey-complete',
    titleKey: 'academy.badges.journeyComplete.title',
    titleDefault: 'Graduate',
    descriptionKey: 'academy.badges.journeyComplete.desc',
    descriptionDefault: 'Complete an entire learning journey',
    icon: 'emoji_events',
  },
  {
    id: 'quiz-ace',
    titleKey: 'academy.badges.quizAce.title',
    titleDefault: 'Quiz Ace',
    descriptionKey: 'academy.badges.quizAce.desc',
    descriptionDefault: 'Score 100% on any quiz',
    icon: 'military_tech',
  },
  {
    id: 'streak-3',
    titleKey: 'academy.badges.streak3.title',
    titleDefault: 'Getting Started',
    descriptionKey: 'academy.badges.streak3.desc',
    descriptionDefault: '3-day learning streak',
    icon: 'local_fire_department',
  },
  {
    id: 'streak-7',
    titleKey: 'academy.badges.streak7.title',
    titleDefault: 'Week Warrior',
    descriptionKey: 'academy.badges.streak7.desc',
    descriptionDefault: '7-day learning streak',
    icon: 'whatshot',
  },
  {
    id: 'streak-14',
    titleKey: 'academy.badges.streak14.title',
    titleDefault: 'Committed Learner',
    descriptionKey: 'academy.badges.streak14.desc',
    descriptionDefault: '14-day learning streak',
    icon: 'bolt',
  },
  {
    id: 'streak-30',
    titleKey: 'academy.badges.streak30.title',
    titleDefault: 'Unstoppable',
    descriptionKey: 'academy.badges.streak30.desc',
    descriptionDefault: '30-day learning streak',
    icon: 'diamond',
  },
  {
    id: 'all-journeys',
    titleKey: 'academy.badges.allJourneys.title',
    titleDefault: 'Renaissance Learner',
    descriptionKey: 'academy.badges.allJourneys.desc',
    descriptionDefault: 'Complete all three learning journeys',
    icon: 'workspace_premium',
  },
  {
    id: 'speed-learner',
    titleKey: 'academy.badges.speedLearner.title',
    titleDefault: 'Speed Learner',
    descriptionKey: 'academy.badges.speedLearner.desc',
    descriptionDefault: 'Complete 5 lessons in one day',
    icon: 'speed',
  },
];

export function getBadge(id: BadgeId): Badge | undefined {
  return BADGE_CATALOG.find((b) => b.id === id);
}

export function checkNewBadges(
  progress: Record<string, LessonProgress>,
  streak: UserStreak | null,
  existingBadges: UserBadge[],
  journeys: JourneyConfig[],
): BadgeId[] {
  const earned = new Set(existingBadges.map((b) => b.badgeId));
  const newBadges: BadgeId[] = [];

  const completedLessons = Object.values(progress).filter((p) => p.status === 'completed');

  if (completedLessons.length >= 1 && !earned.has('first-lesson')) {
    newBadges.push('first-lesson');
  }

  if (streak) {
    if (streak.currentDays >= 3 && !earned.has('streak-3')) newBadges.push('streak-3');
    if (streak.currentDays >= 7 && !earned.has('streak-7')) newBadges.push('streak-7');
    if (streak.currentDays >= 14 && !earned.has('streak-14')) newBadges.push('streak-14');
    if (streak.currentDays >= 30 && !earned.has('streak-30')) newBadges.push('streak-30');
  }

  for (const journey of journeys) {
    for (const mod of journey.modules) {
      const allComplete = mod.lessons.every((l) => progress[l.id]?.status === 'completed');
      if (allComplete && !earned.has('module-complete')) {
        newBadges.push('module-complete');
      }
    }

    const journeyComplete = journey.modules.every((m) =>
      m.lessons.every((l) => progress[l.id]?.status === 'completed'),
    );
    if (journeyComplete && !earned.has('journey-complete')) {
      newBadges.push('journey-complete');
    }
  }

  const allJourneysComplete = journeys.every((j) =>
    j.modules.every((m) => m.lessons.every((l) => progress[l.id]?.status === 'completed')),
  );
  if (allJourneysComplete && !earned.has('all-journeys')) {
    newBadges.push('all-journeys');
  }

  return newBadges;
}
